import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Button, 
  Card, 
  CardContent,
  Chip,
  Paper,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import { keyframes } from '@mui/system';
import backgroundImage from '../Images/clg.png';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const About = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <SchoolIcon fontSize="large" />,
      title: 'Academic Resources',
      description: 'Access comprehensive syllabus, papers, and study materials with smart organization.',
      color: theme.palette.primary.main,
      bgGradient: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`
    },
    {
      icon: <GroupIcon fontSize="large" />,
      title: 'Real-time Collaboration',
      description: 'Connect and collaborate with teachers and peers through interactive platforms.',
      color: theme.palette.secondary.main,
      bgGradient: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`
    },
    {
      icon: <AssignmentIcon fontSize="large" />,
      title: 'Smart Assignments',
      description: 'Submit, track, and manage assignments with intelligent progress monitoring.',
      color: theme.palette.success.main,
      bgGradient: `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
    },
    {
      icon: <ChatIcon fontSize="large" />,
      title: 'AI Assistant',
      description: 'Get instant, intelligent answers to your academic questions 24/7.',
      color: theme.palette.warning.main,
      bgGradient: `linear-gradient(135deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`
    }
  ];

  const stats = [
    { number: '2K+', label: 'Active Students' },
    { number: '250+', label: 'Educators' },
    { number: '95%', label: 'Success Rate' },
    { number: '24/7', label: 'Support Available' }
  ];

  const benefits = [
    { title: 'Student-Centric Design', description: 'Built with students in mind, every feature enhances your learning experience.' },
    { title: 'Educator Empowerment', description: 'Powerful tools that help teachers manage and inspire effectively.' },
    { title: 'AI-Powered Intelligence', description: 'Smart algorithms that adapt to your learning style.' }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Floating Animation Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.1),
          animation: `${float} 3s ease-in-out infinite`,
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '10%',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.05),
          animation: `${float} 4s ease-in-out infinite`,
          animationDelay: '1s',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.08),
          animation: `${pulse} 2s ease-in-out infinite`,
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Chip
            icon={<AutoAwesomeIcon />}
            label="Empowering Education"
            sx={{
              mb: 3,
              background: alpha(theme.palette.common.white, 0.15),
              color: 'white',
              backdropFilter: 'blur(10px)',
              fontSize: '0.9rem',
              fontWeight: 'medium'
            }}
          />
          
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4.5rem', lg: '5.5rem' },
              fontWeight: 900,
              color: 'white',
              mb: 3,
              lineHeight: 1.1,
              textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              animation: `${slideIn} 0.8s ease-out`
            }}
          >
            Welcome to{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              SSPACE Assist
            </Box>
          </Typography>
          
          <Typography
            variant="h4"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 4,
              fontWeight: 300,
              maxWidth: 800,
              mx: 'auto',
              animation: `${slideIn} 0.8s ease-out 0.2s both`
            }}
          >
            Transform your academic journey with our intelligent platform designed for modern learning
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              animation: `${slideIn} 0.8s ease-out 0.4s both`
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => window.location.href = '/login'}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 3,
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayArrowIcon />}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 3,
                borderColor: alpha(theme.palette.common.white, 0.5),
                color: 'white',
                backdropFilter: 'blur(10px)',
                background: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  borderColor: 'white',
                  background: alpha(theme.palette.common.white, 0.2),
                }
              }}
            >
              Watch Demo
            </Button>
          </Box>
        </Box>

        {/* Stats Section */}
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.common.white, 0.15),
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            p: 4,
            mb: 8,
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`
          }}
        >
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      color: 'white',
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' }
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha(theme.palette.common.white, 0.8),
                      fontWeight: 'medium'
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* About Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            icon={<StarIcon />}
            label="About Our Platform"
            sx={{
              mb: 3,
              background: theme.palette.info.main,
              color: 'white',
              fontSize: '0.9rem'
            }}
          />
          
          <Typography
            variant="h2"
            sx={{
              fontWeight: 'bold',
              color: 'white',
              mb: 4,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Revolutionizing Academic Excellence
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 6,
              maxWidth: 900,
              mx: 'auto',
              lineHeight: 1.7
            }}
          >
            SSPACE Assist is more than just an academic platform—it's your partner in educational success. 
            We combine cutting-edge technology with intuitive design to create an environment where 
            learning thrives, collaboration flourishes, and academic goals become achievements.
          </Typography>
          
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    p: 2
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: theme.palette.success.light,
                      mr: 2,
                      mt: 0.5,
                      fontSize: '1.5rem'
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: 'white',
                        mb: 1
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.8),
                        lineHeight: 1.6
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<AutoAwesomeIcon />}
              label="Powerful Features"
              sx={{
                mb: 3,
                background: theme.palette.secondary.main,
                color: 'white',
                fontSize: '0.9rem'
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                color: 'white',
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Everything You Need to Succeed
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Discover the comprehensive suite of tools designed to elevate your academic performance
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    background: alpha(theme.palette.common.white, 0.95),
                    borderRadius: 4,
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                      background: alpha(theme.palette.common.white, 1),
                    }
                  }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mb: 3,
                        background: feature.bgGradient,
                        boxShadow: `0 8px 20px ${alpha(feature.color, 0.3)}`
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: theme.palette.text.primary
                      }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        mb: 2
                      }}
                    >
                      {feature.description}
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: feature.color,
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.text.primary
                        }
                      }}
                    >
                      Learn more
                      <ArrowForwardIcon sx={{ ml: 1, fontSize: '1rem' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.common.white, 0.1),
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            p: 6,
            textAlign: 'center',
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            mb: 4
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 'bold',
              color: 'white',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Ready to Transform Your Learning?
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              mb: 4,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Join thousands of students and educators who have already discovered the power of intelligent academic management.
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              mb: 3
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => window.location.href = '/login'}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 3,
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                }
              }}
            >
              Start Your Journey
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 3,
                borderColor: alpha(theme.palette.common.white, 0.5),
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  background: alpha(theme.palette.common.white, 0.1),
                }
              }}
            >
              Schedule Demo
            </Button>
          </Box>
          
          <Typography
            variant="caption"
            sx={{
              color: alpha(theme.palette.common.white, 0.7),
              fontSize: '0.9rem'
            }}
          >
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default About;